/**
 * ChapterList
 *
 * Sidebar component displaying chapters with drag-to-reorder support.
 * Uses @dnd-kit for accessible drag and drop.
 */

import { useState } from 'react'
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  type DragEndEvent,
} from '@dnd-kit/core'
import {
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable'
import { Plus, Loader2 } from 'lucide-react'
import { ChapterListItem } from './ChapterListItem'
import type { ChapterRecord } from '@/types/writingStudio'

interface ChapterListProps {
  chapters: ChapterRecord[]
  selectedChapterId: string | null
  onSelect: (chapterId: string) => void
  onAdd: () => Promise<void>
  onRename: (chapterId: string, newTitle: string) => Promise<void>
  onDelete: (chapterId: string) => Promise<void>
  onReorder: (orderedIds: string[]) => Promise<void>
  disabled?: boolean
}

export function ChapterList({
  chapters,
  selectedChapterId,
  onSelect,
  onAdd,
  onRename,
  onDelete,
  onReorder,
  disabled = false,
}: ChapterListProps) {
  const [isAdding, setIsAdding] = useState(false)

  // Sort chapters by order
  const sortedChapters = [...chapters].sort((a, b) => a.order - b.order)
  const chapterIds = sortedChapters.map((c) => c.id)

  // Setup drag sensors with keyboard support
  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 8, // 8px movement before drag starts
      },
    }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  )

  const handleDragEnd = async (event: DragEndEvent) => {
    const { active, over } = event

    if (over && active.id !== over.id) {
      const oldIndex = chapterIds.indexOf(active.id as string)
      const newIndex = chapterIds.indexOf(over.id as string)

      // Create new ordered array
      const newOrder = [...chapterIds]
      newOrder.splice(oldIndex, 1)
      newOrder.splice(newIndex, 0, active.id as string)

      await onReorder(newOrder)
    }
  }

  const handleAdd = async () => {
    setIsAdding(true)
    try {
      await onAdd()
    } finally {
      setIsAdding(false)
    }
  }

  return (
    <div className="flex flex-col h-full">
      {/* Header */}
      <div className="flex items-center justify-between px-4 py-3 border-b border-[var(--color-kids-border)]">
        <h3 className="font-display font-bold text-[var(--color-kids-text)]">
          Chapters
        </h3>
        <span className="text-xs text-[var(--color-kids-text-muted)]">
          {chapters.length} {chapters.length === 1 ? 'chapter' : 'chapters'}
        </span>
      </div>

      {/* Chapter list with drag and drop */}
      <div className="flex-1 overflow-y-auto p-2">
        <DndContext
          sensors={sensors}
          collisionDetection={closestCenter}
          onDragEnd={handleDragEnd}
        >
          <SortableContext
            items={chapterIds}
            strategy={verticalListSortingStrategy}
          >
            <div className="space-y-1">
              {sortedChapters.map((chapter) => (
                <ChapterListItem
                  key={chapter.id}
                  chapter={chapter}
                  isSelected={chapter.id === selectedChapterId}
                  onSelect={() => onSelect(chapter.id)}
                  onRename={(newTitle) => onRename(chapter.id, newTitle)}
                  onDelete={() => onDelete(chapter.id)}
                  canDelete={chapters.length > 1}
                  disabled={disabled}
                />
              ))}
            </div>
          </SortableContext>
        </DndContext>
      </div>

      {/* Add chapter button */}
      <div className="p-3 border-t border-[var(--color-kids-border)]">
        <button
          onClick={handleAdd}
          disabled={disabled || isAdding}
          className="w-full flex items-center justify-center gap-2 px-4 py-2.5 rounded-[var(--radius-kid)] bg-[var(--color-kids-primary)] text-white font-medium hover:bg-[var(--color-kids-primary-dark)] disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
        >
          {isAdding ? (
            <Loader2 className="w-4 h-4 animate-spin" />
          ) : (
            <Plus className="w-4 h-4" />
          )}
          Add Chapter
        </button>
      </div>
    </div>
  )
}
